import { ExpandableText } from "../ui/expandable-text";

interface ProjectOverviewContentProps {
  name: string;
  salicNumber: string;
  proponent: string;
  cnpj: string;
  logline: string;
  coverImage?: string;
  sinopse: string;
  minutagem: string;
  episodeCount: string;
  episodeDuration: string;
  productionPlan: string;
  episodeValueUSD?: number;
  totalValueUSD?: number;
  capturedValueUSD?: number;
  toCaptureValueUSD?: number;
  hasFinancing?: boolean;
  financingModel?: string;
  additionalInfo?: string;
}

export function ProjectOverviewContent({
  name,
  salicNumber,
  proponent,
  cnpj,
  logline,
  coverImage,
  sinopse,
  minutagem,
  episodeCount,
  episodeDuration,
  productionPlan,
  episodeValueUSD,
  totalValueUSD,
  capturedValueUSD,
  toCaptureValueUSD,
  hasFinancing,
  financingModel,
  additionalInfo,
}: ProjectOverviewContentProps) {
  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "-";
    return `$ ${value.toLocaleString("en-US")}`;
  };

  return (
    <div className="flex flex-col gap-[30px] items-start w-full">
      {/* Project Title Section */}
      <div className="flex flex-col gap-[10px] items-start w-full">
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[24px] w-[989px]">
          Visão Geral do Projeto - {name}
        </p>
        <div className="flex gap-[10px] items-start font-['Plus_Jakarta_Sans'] font-normal text-[#d1d1d1] text-[14px] leading-[0] whitespace-pre w-full">
          <p className="leading-normal">
            <span>Número Salic: </span>
            <span className="font-semibold text-[#f6f6f6]">{salicNumber}</span>
          </p>
          <p className="leading-normal">
            <span>Produtora: </span>
            <span className="font-semibold text-[#f6f6f6]">
              {proponent} | {cnpj}
            </span>
          </p>
        </div>
        <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#f6f6f6] text-[14px] w-full">
          {logline}
        </p>
      </div>

      {/* Cover Image */}
      {coverImage && (
        <div className="h-[263px] w-full rounded-[10px] overflow-hidden">
          <img
            src={coverImage}
            alt={name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Sinopse Section */}
      <div className="flex flex-col gap-[6px] items-start w-full text-[16px]">
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white w-[989px]">
          Sinopse:
        </p>
        <ExpandableText text={sinopse} maxLength={500} />
      </div>

      {/* Production Info Grid */}
      <div className="flex gap-[85px] items-start w-full">
        <div className="flex flex-col gap-[2px] items-start w-[265px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Minutagem:
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {minutagem || "-"}
          </p>
        </div>
        <div className="flex flex-col gap-[2px] items-start w-[310px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Número de episódios:
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {episodeCount || "-"}
          </p>
        </div>
        <div className="flex flex-col gap-[2px] items-start w-[328px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Minutagem de cada episódio:
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {episodeDuration || "-"}
          </p>
        </div>
      </div>

      {/* Production Plan Section */}
      <div className="flex flex-col gap-[6px] items-start w-full">
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
          Plano de Produção - Detalhar seu cronograma e estrutura do projeto:
        </p>
        <ExpandableText text={productionPlan} maxLength={800} />
      </div>

      {/* Financial Info Grid */}
      <div className="flex gap-[85px] items-start w-full">
        <div className="flex flex-col gap-[2px] items-start w-[196px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Valor por episódio (USD):
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {formatCurrency(episodeValueUSD)}
          </p>
        </div>
        <div className="flex flex-col gap-[2px] items-start w-[219px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Valor total do projeto (USD):
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {formatCurrency(totalValueUSD)}
          </p>
        </div>
        <div className="flex flex-col gap-[2px] items-start w-[181px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Valor já captado (USD):
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {formatCurrency(capturedValueUSD)}
          </p>
        </div>
        <div className="flex flex-col gap-[2px] items-start w-[161px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Valor a captar (USD):
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {formatCurrency(toCaptureValueUSD)}
          </p>
        </div>
      </div>

      {/* Financing Questions */}
      <div className="flex gap-[85px] items-start w-full">
        <div className="flex flex-col gap-[2px] items-start w-[196px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Já possui algum tipo de financiamento?
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
            {hasFinancing !== undefined ? (hasFinancing ? "Sim" : "Não") : "-"}
          </p>
        </div>
      </div>

      {financingModel && (
        <div className="flex gap-[85px] items-start w-full">
          <div className="flex flex-col gap-[2px] items-start w-[196px]">
            <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
              Qual modelo? (aprovado em lei de incentivo, edital, patrocinador, etc)
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[1.4] text-[#d1d1d1] text-[14px] w-full">
              {financingModel}
            </p>
          </div>
        </div>
      )}

      {/* Additional Info */}
      {additionalInfo && (
        <div className="flex flex-col gap-[6px] items-start w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-white text-[16px] w-[989px]">
            Informação Adicional:
          </p>
          <ExpandableText text={additionalInfo} maxLength={500} />
        </div>
      )}
    </div>
  );
}
